import React, {
  createContext,
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Nav from "@ui/NavV2";
import Tab from "@ui/NavV2/Tab";
import { useNavigate, useParams } from "react-router";
import { toPath } from "../../../../../../../router/helpers/path";
import t from "../../../../../../../i18n";
import { css } from "@linaria/core";
import { colors, shadows, spacings } from "@ui/Theme";
import {
  useCreateAndLinkEventDashboard,
  useEventDashboards,
  useLinkEventDashboards,
  useUnlinkEventDashboards,
  type EventDashboard,
} from "../../../../../../../api/events/dashboards";
import {
  AddCircleLine,
  ArrowRightDoubleLine,
  CloseLine,
  LinkM,
  ShareBoxLine,
} from "@ui/IconV2/react";
import { usePublishedDashboards } from "../../../../../Dashboards/hook/api/dashboard";
import TooltipV2 from "@ui/TooltipV2";
import ButtonIconV2 from "@ui/ButtonIconV2";
import Popper from "@ui/Popper";
import { useTranslations } from "../../../../../../../api/useTranslations";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { FragmentSlot, OptionV2 } from "@ui/Option/v2";
import Checkbox from "@ui/Checkbox";
import Button from "@ui/Button";
import Text from "@ui/Text";
import useToasts from "@ui/Toast/useToasts";
import { toBasenamePath } from "../../../../../../../router/helpers/navigation";
import Modal from "@ui/Modal";
import ModalForm from "@ui/ModalV2/Form";
import Menu from "@ui/Menu";
import OptionList from "@ui/OptionList";
import { useCanUserDoOnProject } from "../../../../../../canUserDo";
import {
  useEventPath,
  type EventPathParameters,
  type EventPaths,
} from "../../../../hooks/useEventPath";
import { filterPublishedDashboards } from "../../../../../Dashboards/utils/search";

const EventDashboardToUnlinkContext = createContext<{
  eventDashboardToUnlink: EventDashboard | null;
  setEventDashboardToUnlink: (eventDashboard: EventDashboard | null) => void;
}>({
  eventDashboardToUnlink: null,
  setEventDashboardToUnlink: (_: EventDashboard | null) => {},
});

export const EventNavigation = () => {
  const { dashboardId } = useParams();
  const { eventPath, eventPathParameters } = useEventPath();

  const translations = useTranslations();
  const eventDashboards = useEventDashboards(eventPathParameters);
  const [eventDashboardToUnlink, setEventDashboardToUnlink] = useState<EventDashboard | null>(null);

  const [overflowTabs, setOverflowTabs] = useState<string[]>([]);
  const eventNavigationRef = useRef<HTMLDivElement>(null);
  const eventNavigationDashboardActionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!eventNavigationRef.current) return;
    const tabs = Array.from(eventNavigationRef.current.children).filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.classList.contains("eventNavigationTab"),
    );
    const tabWs = new WeakMap(tabs.map(tab => [tab, tab.offsetLeft + tab.offsetWidth]));

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry || !(entry?.target instanceof HTMLElement)) return;
      const maxW =
        entry.target.offsetLeft +
        entry.contentRect.width -
        (eventNavigationDashboardActionsRef.current?.clientWidth ?? 0);
      const overflowTabMatches: string[] = [];
      for (const tab of tabs) {
        if ((tabWs.get(tab) ?? -Infinity) > maxW) {
          tab.style.visibility = "hidden";
          const id = tab.getAttribute("data-event-dashboard-id");
          if (id) overflowTabMatches.push(id);
          continue;
        }
        tab.style.visibility = "visible";
      }
      setOverflowTabs(tabs => {
        if (
          tabs.length === overflowTabMatches.length &&
          tabs.every(id => overflowTabMatches.includes(id))
        ) {
          return tabs;
        }
        return overflowTabMatches;
      });
    });
    ro.observe(eventNavigationRef.current);
    return () => ro.disconnect();
  }, [eventDashboards]);

  const overflowEventDashboards = useMemo(
    () =>
      eventDashboards.filter(eventDashboard => overflowTabs.includes(eventDashboard.dashboardId)),
    [overflowTabs, eventDashboards],
  );

  return (
    <Nav ref={eventNavigationRef} className={eventNavigation}>
      <Tab className="eventNavigationTab" to={toPath(`${eventPath}/fault`, eventPathParameters)}>
        {t("project.fault_event.tab.fault")}
      </Tab>
      <Tab className="eventNavigationTab" to={toPath(`${eventPath}/symptom`, eventPathParameters)}>
        {t("project.fault_event.tab.symptom")}
      </Tab>
      <Tab className="eventNavigationTab" to={toPath(`${eventPath}/data`, eventPathParameters)}>
        {t("project.fault_event.tab.data")}
      </Tab>
      <div className={divider} />
      <EventDashboardToUnlinkContext.Provider
        value={{
          eventDashboardToUnlink,
          setEventDashboardToUnlink: eventDashboard => setEventDashboardToUnlink(eventDashboard),
        }}
      >
        {eventDashboards.map(eventDashboard => (
          <Tab
            data-event-dashboard-id={eventDashboard.dashboardId}
            className="eventNavigationTab"
            disabled={overflowEventDashboards.some(
              overflowEventDashboard =>
                overflowEventDashboard.dashboardId === eventDashboard.dashboardId,
            )}
            key={eventDashboard.dashboardId}
            to={toPath(`${eventPath}/dashboard/:dashboardId`, {
              ...eventPathParameters,
              dashboardId: eventDashboard.dashboardId,
            })}
          >
            <LinkM size="s" />
            {eventDashboard.dashboardTitle}
            <UnlinkEventDashboardButton eventDashboard={eventDashboard} />
          </Tab>
        ))}
        <div ref={eventNavigationDashboardActionsRef} className={eventNavigationDashboardActions}>
          {overflowEventDashboards.length > 0 && (
            <OverflowDashboardListPopover
              dashboardId={dashboardId}
              eventDashboards={overflowEventDashboards}
              eventPath={eventPath}
              eventPathParameters={eventPathParameters}
            >
              {({ overflowDashboardListOpen }) => (
                <TooltipV2
                  placement="top"
                  overlay={t(
                    "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.see_more",
                  )}
                >
                  <ButtonIconV2>
                    <ArrowRightDoubleLine
                      color={
                        overflowEventDashboards.some(
                          eventDashboard => eventDashboard.dashboardId === dashboardId,
                        ) || overflowDashboardListOpen
                          ? colors.lightNitrogen500_Main
                          : colors.carbon800
                      }
                      size="m"
                    />
                  </ButtonIconV2>
                </TooltipV2>
              )}
            </OverflowDashboardListPopover>
          )}
          <AvailableDashboardListPopover eventPathParameters={eventPathParameters}>
            {({ openDashboardList }) => (
              <TooltipV2
                placement="top"
                overlay={t(
                  "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.add_dashboard",
                  translations,
                )}
              >
                <ButtonIconV2 onClick={openDashboardList}>
                  <AddCircleLine color={colors.carbon800} size="m" />
                </ButtonIconV2>
              </TooltipV2>
            )}
          </AvailableDashboardListPopover>
        </div>
        <UnlinkEventDashboardModal
          displayedDashboardId={dashboardId}
          eventPath={eventPath}
          eventPathParameters={eventPathParameters}
        />
      </EventDashboardToUnlinkContext.Provider>
    </Nav>
  );
};

const UnlinkEventDashboardButton = ({
  eventDashboard,
  onClick,
}: {
  eventDashboard: EventDashboard;
  onClick?: () => void;
}) => {
  const { setEventDashboardToUnlink } = useContext(EventDashboardToUnlinkContext);

  return (
    <>
      <TooltipV2
        placement="top"
        overlay={t(
          "project.fault_event.header.navigation.dashboard_tab.tooltip.icon.remove_dashboard",
        )}
      >
        <ButtonIconV2
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setEventDashboardToUnlink(eventDashboard);
            onClick?.();
          }}
        >
          <CloseLine color={colors.carbon800} size="s" />
        </ButtonIconV2>
      </TooltipV2>
    </>
  );
};

const UnlinkEventDashboardModal = ({
  displayedDashboardId,
  eventPath,
  eventPathParameters,
}: {
  displayedDashboardId: string | undefined;
  eventPath: EventPaths;
  eventPathParameters: EventPathParameters;
}) => {
  const { eventDashboardToUnlink, setEventDashboardToUnlink } = useContext(
    EventDashboardToUnlinkContext,
  );
  const translations = useTranslations();
  const { addToast } = useToasts();
  const eventDashboards = useEventDashboards(eventPathParameters);
  const navigate = useNavigate();

  const closeModal = useCallback(
    () => setEventDashboardToUnlink(null),
    [setEventDashboardToUnlink],
  );
  const unlinkEventDashboards = useUnlinkEventDashboards(eventPathParameters);
  const unlinkEventDashboard = useCallback(() => {
    if (!eventDashboardToUnlink) return false;
    const currentDashboardIndex = eventDashboards.findIndex(
      eventDashboard => eventDashboard.dashboardId === eventDashboardToUnlink.dashboardId,
    );
    const previousEventDashboard = eventDashboards[currentDashboardIndex - 1];
    return unlinkEventDashboards([eventDashboardToUnlink.dashboardId])
      .then(() => {
        if (eventDashboardToUnlink.dashboardId === displayedDashboardId) {
          const path = previousEventDashboard
            ? toPath(`${eventPath}/dashboard/:dashboardId`, {
                ...eventPathParameters,
                dashboardId: previousEventDashboard.dashboardId,
              })
            : toPath(`${eventPath}/data`, eventPathParameters);

          return (
            navigate(path)?.then(() => {
              setEventDashboardToUnlink(null);
              return true;
            }) ?? true
          );
        } else {
          closeModal();
          return true;
        }
      })
      .catch(() => {
        addToast("error", t("errors.backend_error"));
        return false;
      });
  }, [
    addToast,
    eventDashboardToUnlink,
    unlinkEventDashboards,
    eventDashboards,
    eventPath,
    eventPathParameters,
    navigate,
    setEventDashboardToUnlink,
    closeModal,
    displayedDashboardId,
  ]);

  return (
    <Modal
      title={t(
        "project.fault_event.header.navigation.modal_form.remove_dashboard.title",
        translations,
      )}
      isOpen={Boolean(eventDashboardToUnlink)}
      onRequestClose={closeModal}
    >
      <ModalForm
        onSubmit={unlinkEventDashboard}
        onRequestClose={closeModal}
        okButtonName={t("modalForm.button.name.remove")}
        okButtonVariant="destructive"
      >
        <Text>
          {t(
            "project.fault_event.header.navigation.modal_form.remove_dashboard.text",
            {
              dashboardName: (
                <Text type="body1SemiBold" as="span">
                  {eventDashboardToUnlink?.dashboardTitle}
                </Text>
              ),
            },
            translations,
          )}
        </Text>
      </ModalForm>
    </Modal>
  );
};

const OverflowDashboardListPopover = ({
  children,
  dashboardId,
  eventPath,
  eventPathParameters,
  eventDashboards,
}: {
  children: ({
    overflowDashboardListOpen,
  }: {
    overflowDashboardListOpen: boolean;
  }) => React.ReactNode;
  dashboardId: string | undefined;
  eventPath: EventPaths;
  eventPathParameters: EventPathParameters;
  eventDashboards: EventDashboard[];
}) => {
  const navigate = useNavigate();
  const canEdit = useCanUserDoOnProject("PROJECT_WRITE", eventPathParameters.projectId);
  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  return (
    <Menu
      className="menu-ref"
      opened={menuOpened}
      setOpened={setMenuOpened}
      overlay={
        <OptionList onRequestClose={() => {}}>
          {eventDashboards.map(eventDashboard => (
            <OptionV2
              key={eventDashboard.dashboardId}
              onClick={() => {
                setMenuOpened(false);
                void navigate(
                  toPath(`${eventPath}/dashboard/:dashboardId`, {
                    ...eventPathParameters,
                    dashboardId: eventDashboard.dashboardId,
                  }),
                );
              }}
              selected={dashboardId === eventDashboard.dashboardId}
              closeOnClick
            >
              <FragmentSlot slot="text">{eventDashboard.dashboardTitle}</FragmentSlot>
              {canEdit && (
                <FragmentSlot slot="extra">
                  <UnlinkEventDashboardButton
                    eventDashboard={eventDashboard}
                    onClick={() => setMenuOpened(false)}
                  />
                </FragmentSlot>
              )}
              {eventDashboard.dashboardDescription &&
                eventDashboard.dashboardDescription.length > 0 && (
                  <FragmentSlot slot="description">
                    {eventDashboard.dashboardDescription}
                  </FragmentSlot>
                )}
            </OptionV2>
          ))}
        </OptionList>
      }
    >
      <div className={eventDashboardListPopoverTrigger}>
        {children({ overflowDashboardListOpen: menuOpened })}
      </div>
    </Menu>
  );
};

const AvailableDashboardListPopover = ({
  children,
  eventPathParameters,
}: {
  children: ({ openDashboardList }: { openDashboardList: () => void }) => React.ReactNode;
  eventPathParameters: EventPathParameters;
}) => {
  const translations = useTranslations();
  const publishedDashboards = usePublishedDashboards(eventPathParameters.projectId);
  const eventDashboards = useEventDashboards(eventPathParameters);
  const { addToast } = useToasts();

  const [dashboardListOpen, setDashboardListOpen] = useState<boolean>(false);
  const openDashboardList = useCallback(() => setDashboardListOpen(true), []);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const dashboardSearchResults = useMemo(
    () => filterPublishedDashboards(publishedDashboards, searchQuery),
    [publishedDashboards, searchQuery],
  );
  const dashboardList = useDeferredValue(dashboardSearchResults);

  const linkEventDashboards = useLinkEventDashboards(eventPathParameters);
  const linkEventDashboard = useCallback(
    (dashboardId: string) =>
      linkEventDashboards([dashboardId]).catch(() => addToast("error", t("errors.backend_error"))),
    [addToast, linkEventDashboards],
  );

  const createAndLinkEventDashboard = useCreateAndLinkEventDashboard(eventPathParameters);
  const createAndLinkDashboard = useCallback(() => {
    void createAndLinkEventDashboard(t("project.dashboard.title.empty"))
      .then(dashboardId => {
        const path = toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
          projectId: eventPathParameters.projectId,
          dashboardId,
        });
        window.open(toBasenamePath(path));
      })
      .catch(() => addToast("error", t("errors.backend_error")));
  }, [addToast, createAndLinkEventDashboard, eventPathParameters]);

  return (
    <Popper
      opened={dashboardListOpen}
      setOpened={setDashboardListOpen}
      className={dashboardSelect}
      closeOnOutsideClick
      placement="bottom-start"
      gap={4}
      overlay={
        <div className={dashboardSelectOverlay}>
          {publishedDashboards.length > 0 && (
            <header>
              <SearchInputV2
                className={dashboardSelectSearch}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
              />
            </header>
          )}
          <div>
            {publishedDashboards.length === 0 && (
              <Text type="body2" className={dashboardSelectMessage}>
                {t("project.fault_event.dashboard_select.empty", translations)}
              </Text>
            )}
            {publishedDashboards.length > 0 && dashboardSearchResults.length === 0 && (
              <Text type="body2" className={dashboardSelectMessage}>
                {t("common.search.no_result")}
              </Text>
            )}
            {dashboardList.map(dashboard => (
              <OptionV2
                key={dashboard.id}
                disabled={eventDashboards.some(d => d.dashboardId === dashboard.id)}
                withTextEllipsis
              >
                <Checkbox
                  slot="checkbox"
                  smallIndicator
                  checked={eventDashboards.some(d => d.dashboardId === dashboard.id)}
                  value={dashboard.id}
                  onChange={e => linkEventDashboard(e.target.value)}
                />
                <FragmentSlot slot="text">{dashboard.title}</FragmentSlot>
                <FragmentSlot slot="description">{dashboard.description}</FragmentSlot>
              </OptionV2>
            ))}
          </div>
          <hr />
          <footer>
            <Button
              variant="link"
              leftIcon={<ShareBoxLine />}
              slim
              className={dashboardSelectCreateShortcut}
              onClick={createAndLinkDashboard}
            >
              {t("project.fault_event.dashboard_select.create", translations)}
            </Button>
          </footer>
        </div>
      }
    >
      <div className={eventDashboardListPopoverTrigger}>{children({ openDashboardList })}</div>
    </Popper>
  );
};

//#region CSS
const eventNavigation = css`
  position: relative;
  overflow-x: hidden;
  background-color: ${colors.titanium50};
`;

const divider = css`
  background-color: ${colors.titanium200};
  width: 1px;
`;
const dashboardSelect = css`
  border-radius: 4px;
  box-shadow: ${shadows.shadow100};
  width: 440px;
`;

const dashboardSelectOverlay = css`
  border-radius: 4px;
  max-height: min(40vh, 450px);
  overflow-y: auto;
  position: relative;

  > :where(header, div, footer) {
    padding: ${spacings.s};
    background: ${colors.hydrogen};
  }

  > :where(header, footer) {
    position: sticky;
    z-index: 1;
  }

  > header {
    top: 0;
  }

  > footer {
    bottom: 0;
  }

  > hr {
    background: ${colors.titanium100};
    border: none;
    height: 1px;
    margin: 0 ${spacings.s};
  }
`;

const dashboardSelectSearch = css`
  width: 100%;
`;

const dashboardSelectCreateShortcut = css`
  display: block;
  margin: auto;
`;

const dashboardSelectMessage = css`
  color: ${colors.carbon700};
  text-align: center;
`;

const eventDashboardListPopoverTrigger = css`
  align-self: center;
`;

const eventNavigationDashboardActions = css`
  display: flex;
  position: sticky;
  right: 0;
  /* outline: 2px solid ${colors.hydrogen}; */
  margin-left: 2px; /* outline offset */
  z-index: 1;
`;
//#endregion CSS
